import { useRef, useState, type DragEvent } from 'react'
import {
  Sparkles,
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle2,
  AlertTriangle,
  Search,
  X,
  Loader2,
  Wand2,
  Eye,
} from 'lucide-react'

const API = '' // ruta relativa — el proxy de Vite reenvía a Next

type TabKey = 'auto' | 'import' | 'preview'

interface AutoResult {
  ok: true
  updated: number
  skipped: number
  total: number
  errors: { id: string; sku: string; message: string }[]
}

interface PreviewItem {
  id: string
  sku: string
  name: string
  meta_title?: string
  meta_description?: string
  meta_keywords?: string
  tags?: string
  slug?: string
}

interface PreviewResult {
  ok: true
  preview: PreviewItem[]
  updated: 0
  skipped: number
  total: number
  errors: { id: string; sku: string; message: string }[]
}

interface ImportResult {
  ok: true
  updated: number
  notFound: string[]
  errors: { row: number; sku: string; message: string }[]
}

const METATAGS_TEMPLATE = `sku,meta_title,meta_description,meta_keywords,tags
LH3K-001,Pastilla de freno delantera | Maz Autopartes,Pastilla de freno delantera de alta calidad para...,pastilla freno delantera,freno,delantero
LH3K-002,Filtro de aceite premium | Maz Autopartes,Filtro de aceite de larga duración compatible con...,filtro aceite,filtro,aceite
`

function downloadTemplate(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function SeoTools() {
  const [tab, setTab] = useState<TabKey>('auto')

  return (
    <div className="space-y-6 p-6">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-extrabold text-ink-900">
          <Sparkles className="size-6 text-brand-600" />
          Herramientas SEO
        </h1>
        <p className="text-sm text-ink-500">
          Genera meta tags automáticamente, importa desde CSV o previsualiza un producto.
        </p>
      </header>

      <div className="flex gap-2 border-b border-ink-200">
        <TabButton
          active={tab === 'auto'}
          onClick={() => setTab('auto')}
          icon={<Wand2 className="size-4" />}
          label="Auto-generar"
        />
        <TabButton
          active={tab === 'import'}
          onClick={() => setTab('import')}
          icon={<Upload className="size-4" />}
          label="Importar metatags CSV"
        />
        <TabButton
          active={tab === 'preview'}
          onClick={() => setTab('preview')}
          icon={<Eye className="size-4" />}
          label="Vista previa"
        />
      </div>

      {tab === 'auto' && <AutoTab />}
      {tab === 'import' && <ImportTab />}
      {tab === 'preview' && <PreviewTab />}
    </div>
  )
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${
        active
          ? 'border-brand-600 text-brand-700'
          : 'border-transparent text-ink-500 hover:text-ink-700'
      }`}
    >
      {icon}
      {label}
    </button>
  )
}

/* ------------------------------------------------------------------ */
/* Tab: Auto-generar                                                   */
/* ------------------------------------------------------------------ */

function AutoTab() {
  const [metaTitle, setMetaTitle] = useState(true)
  const [metaDescription, setMetaDescription] = useState(true)
  const [metaKeywords, setMetaKeywords] = useState(true)
  const [tags, setTags] = useState(true)
  const [slug, setSlug] = useState(false)
  const [scope, setScope] = useState<'missing-only' | 'all'>('missing-only')
  const [limit, setLimit] = useState(1000)
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<AutoResult | null>(null)
  const [error, setError] = useState('')

  const anyFlag = metaTitle || metaDescription || metaKeywords || tags || slug

  async function run() {
    if (!anyFlag) return
    setRunning(true)
    setError('')
    setResult(null)
    try {
      const res = await fetch(`${API}/api/admin/seo/auto-generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          scope,
          limit,
          generate: {
            metaTitle,
            metaDescription,
            metaKeywords,
            tags,
            slug,
          },
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) {
        setError(data.error || `HTTP ${res.status}`)
        return
      }
      setResult(data as AutoResult)
    } catch (e: any) {
      setError(e?.message || 'Error de red')
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border border-ink-200 bg-white p-5">
        <h2 className="mb-3 font-semibold text-ink-900">1. ¿Qué campos quieres generar?</h2>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <FieldCheckbox
            label="meta_title"
            description="Título SEO (max 150 chars)"
            checked={metaTitle}
            onChange={setMetaTitle}
          />
          <FieldCheckbox
            label="meta_description"
            description="Descripción para buscadores (max 320 chars)"
            checked={metaDescription}
            onChange={setMetaDescription}
          />
          <FieldCheckbox
            label="meta_keywords"
            description="Palabras clave separadas por coma"
            checked={metaKeywords}
            onChange={setMetaKeywords}
          />
          <FieldCheckbox
            label="tags"
            description="Etiquetas limpias (marca, categoría, año)"
            checked={tags}
            onChange={setTags}
          />
          <FieldCheckbox
            label="slug"
            description="URL amigable basada en nombre (cuidado: cambia URLs)"
            checked={slug}
            onChange={setSlug}
          />
        </div>
      </section>

      <section className="rounded-2xl border border-ink-200 bg-white p-5">
        <h2 className="mb-3 font-semibold text-ink-900">2. Alcance</h2>
        <div className="space-y-2">
          <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-ink-200 p-3 hover:bg-ink-50">
            <input
              type="radio"
              name="scope"
              value="missing-only"
              checked={scope === 'missing-only'}
              onChange={() => setScope('missing-only')}
              className="mt-1"
            />
            <div>
              <div className="font-semibold text-ink-900">Solo donde falta</div>
              <div className="text-xs text-ink-500">
                Sólo procesa productos cuyo campo seleccionado esté vacío o nulo.
              </div>
            </div>
          </label>
          <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-ink-200 p-3 hover:bg-ink-50">
            <input
              type="radio"
              name="scope"
              value="all"
              checked={scope === 'all'}
              onChange={() => setScope('all')}
              className="mt-1"
            />
            <div>
              <div className="font-semibold text-ink-900">Todos los productos</div>
              <div className="text-xs text-ink-500">
                Sobrescribe valores existentes con los generados automáticamente.
              </div>
            </div>
          </label>
        </div>

        <div className="mt-4">
          <label className="text-sm font-semibold text-ink-700">Límite</label>
          <input
            type="number"
            min={1}
            max={50000}
            value={limit}
            onChange={(e) => setLimit(Math.max(1, parseInt(e.target.value) || 1))}
            className="mt-1 w-40 rounded-lg border border-ink-200 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          />
          <p className="mt-1 text-xs text-ink-500">
            Máximo de productos a procesar en una corrida (default 1000).
          </p>
        </div>
      </section>

      <section className="rounded-2xl border border-ink-200 bg-white p-5">
        <button
          type="button"
          onClick={run}
          disabled={running || !anyFlag}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {running ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Wand2 className="size-4" />
          )}
          {running ? 'Procesando…' : 'Generar'}
        </button>
        {!anyFlag && (
          <p className="mt-2 text-xs text-amber-700">
            Selecciona al menos un campo a generar.
          </p>
        )}
      </section>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 size-4" />
          {error}
        </div>
      )}

      {result && (
        <section className="rounded-2xl border border-ink-200 bg-white p-5">
          <div className="mb-4 flex items-center gap-2 border-b border-ink-100 pb-3">
            <CheckCircle2 className="size-5 text-emerald-600" />
            <h2 className="font-semibold text-ink-900">Resultado</h2>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Procesados" value={result.total} color="brand" />
            <Stat label="Actualizados" value={result.updated} color="emerald" />
            <Stat
              label="Sin cambios"
              value={result.skipped}
              color={result.skipped ? 'amber' : 'ink'}
            />
            <Stat
              label="Errores"
              value={result.errors.length}
              color={result.errors.length ? 'red' : 'ink'}
            />
          </div>

          {result.errors.length > 0 && (
            <ListSection
              title="Errores"
              variant="red"
              items={result.errors.map(
                (e) => `${e.sku || '(sin sku)'} (id ${e.id}): ${e.message}`,
              )}
            />
          )}
        </section>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Tab: Importar metatags CSV                                          */
/* ------------------------------------------------------------------ */

function ImportTab() {
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [error, setError] = useState('')

  async function upload() {
    if (!file) return
    setUploading(true)
    setError('')
    setResult(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await fetch(`${API}/api/admin/seo/import-metatags`, {
        method: 'POST',
        credentials: 'same-origin',
        body: fd,
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) {
        setError(data.error || `HTTP ${res.status}`)
        return
      }
      setResult(data as ImportResult)
    } catch (e: any) {
      setError(e?.message || 'Error de red')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-5">
      <TemplateCard
        title="1. Descarga la plantilla"
        description='Columnas: "sku" (requerido), "meta_title", "meta_description", "meta_keywords", "tags". Sólo los campos no-vacíos se actualizan.'
        filename="plantilla-metatags.csv"
        onDownload={() => downloadTemplate('plantilla-metatags.csv', METATAGS_TEMPLATE)}
      />

      <DropZone
        file={file}
        onFile={setFile}
        onUpload={upload}
        uploading={uploading}
        error={error}
        hint="CSV con sku + algún campo metatag"
      />

      {result && (
        <section className="rounded-2xl border border-ink-200 bg-white p-5">
          <div className="mb-4 flex items-center gap-2 border-b border-ink-100 pb-3">
            <CheckCircle2 className="size-5 text-emerald-600" />
            <h2 className="font-semibold text-ink-900">Resultado</h2>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Actualizados" value={result.updated} color="emerald" />
            <Stat
              label="SKUs no encontrados"
              value={result.notFound.length}
              color={result.notFound.length ? 'amber' : 'ink'}
            />
            <Stat
              label="Errores"
              value={result.errors.length}
              color={result.errors.length ? 'red' : 'ink'}
            />
          </div>

          {result.notFound.length > 0 && (
            <ListSection
              title="SKUs no encontrados"
              variant="amber"
              items={result.notFound}
            />
          )}

          {result.errors.length > 0 && (
            <ListSection
              title="Errores"
              variant="red"
              items={result.errors.map(
                (e) => `Fila ${e.row} · ${e.sku || '(sin sku)'}: ${e.message}`,
              )}
            />
          )}
        </section>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Tab: Vista previa                                                   */
/* ------------------------------------------------------------------ */

function PreviewTab() {
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [preview, setPreview] = useState<PreviewItem | null>(null)

  async function run() {
    const q = query.trim()
    if (!q) {
      setError('Ingresa un SKU o slug')
      return
    }
    setLoading(true)
    setError('')
    setPreview(null)
    try {
      // Primero resuelve el SKU/slug → ID. Usamos productos GET para buscar por SKU
      // y luego mandamos productIds al auto-generate con dry_run.
      // Estrategia simple: pedimos productIds=[q] suponiendo que sea ID. Si falla,
      // intentamos resolver por SKU consultando productos con q=...
      let productId: string | null = null

      // Intento 1: si q es numérico, probablemente es ID
      if (/^\d+$/.test(q)) {
        productId = q
      } else {
        // Intento 2: buscar producto por SKU en /api/admin/productos?q=
        const searchRes = await fetch(
          `${API}/api/admin/productos?q=${encodeURIComponent(q)}`,
          { credentials: 'same-origin' },
        )
        const searchData = await searchRes.json().catch(() => ({}))
        if (searchRes.ok && Array.isArray(searchData?.data)) {
          // Buscar match exacto por SKU o slug
          const lower = q.toLowerCase()
          const exact = searchData.data.find(
            (p: any) => p.sku?.toLowerCase() === lower,
          )
          const first = exact ?? searchData.data[0]
          if (first?.id) productId = String(first.id)
        }
      }

      if (!productId) {
        setError(`No se encontró producto con SKU/slug "${q}"`)
        return
      }

      const res = await fetch(`${API}/api/admin/seo/auto-generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          productIds: [productId],
          dry_run: true,
          scope: 'all',
          generate: {
            metaTitle: true,
            metaDescription: true,
            metaKeywords: true,
            tags: true,
            slug: true,
          },
        }),
      })
      const data = (await res.json().catch(() => ({}))) as PreviewResult & {
        error?: string
      }
      if (!res.ok || !data.ok) {
        setError(data.error || `HTTP ${res.status}`)
        return
      }
      if (!data.preview || data.preview.length === 0) {
        setError('No se generó preview (¿el producto existe?)')
        return
      }
      setPreview(data.preview[0]!)
    } catch (e: any) {
      setError(e?.message || 'Error de red')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border border-ink-200 bg-white p-5">
        <h2 className="mb-3 font-semibold text-ink-900">Buscar producto</h2>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') run()
              }}
              placeholder="SKU o ID del producto"
              className="w-full rounded-lg border border-ink-200 py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none"
            />
          </div>
          <button
            type="button"
            onClick={run}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Eye className="size-4" />
            )}
            Previsualizar
          </button>
        </div>
        <p className="mt-2 text-xs text-ink-500">
          La previsualización NO modifica nada — sólo te muestra cómo quedaría el SEO.
        </p>
      </section>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 size-4" />
          {error}
        </div>
      )}

      {preview && (
        <section className="space-y-3 rounded-2xl border border-ink-200 bg-white p-5">
          <div className="border-b border-ink-100 pb-3">
            <div className="text-xs uppercase tracking-wider text-ink-500">
              Producto
            </div>
            <div className="font-semibold text-ink-900">{preview.name}</div>
            <div className="text-xs text-ink-500">
              SKU: <span className="font-mono">{preview.sku}</span> · ID:{' '}
              <span className="font-mono">{preview.id}</span>
            </div>
          </div>

          <PreviewField label="meta_title" value={preview.meta_title} />
          <PreviewField
            label="meta_description"
            value={preview.meta_description}
          />
          <PreviewField label="meta_keywords" value={preview.meta_keywords} />
          <PreviewField label="tags" value={preview.tags} />
          <PreviewField label="slug" value={preview.slug} mono />
        </section>
      )}
    </div>
  )
}

function PreviewField({
  label,
  value,
  mono,
}: {
  label: string
  value?: string
  mono?: boolean
}) {
  if (value === undefined || value === null || value === '') {
    return (
      <div>
        <div className="text-xs uppercase tracking-wider text-ink-500">
          {label}
        </div>
        <div className="text-sm italic text-ink-400">— (sin cambios)</div>
      </div>
    )
  }
  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-ink-500">
          {label}
        </div>
        <div className="text-xs text-ink-400">{value.length} chars</div>
      </div>
      <div
        className={`mt-1 rounded-lg border border-ink-200 bg-ink-50 p-2 text-sm ${mono ? 'font-mono' : ''} text-ink-900`}
      >
        {value}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Sub-componentes compartidos                                          */
/* ------------------------------------------------------------------ */

function FieldCheckbox({
  label,
  description,
  checked,
  onChange,
}: {
  label: string
  description: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label
      className={`flex cursor-pointer items-start gap-2 rounded-lg border p-3 transition-colors ${
        checked
          ? 'border-brand-300 bg-brand-50/50'
          : 'border-ink-200 hover:bg-ink-50'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1"
      />
      <div>
        <div className="font-mono text-sm font-semibold text-ink-900">
          {label}
        </div>
        <div className="text-xs text-ink-500">{description}</div>
      </div>
    </label>
  )
}

function TemplateCard({
  title,
  description,
  filename,
  onDownload,
}: {
  title: string
  description: string
  filename: string
  onDownload: () => void
}) {
  return (
    <section className="rounded-2xl border border-ink-200 bg-white p-5">
      <div className="flex items-start gap-3">
        <FileSpreadsheet className="mt-0.5 size-5 text-brand-600" />
        <div className="flex-1">
          <h2 className="font-semibold text-ink-900">{title}</h2>
          <p className="text-sm text-ink-500">{description}</p>
          <button
            type="button"
            onClick={onDownload}
            className="mt-2 inline-flex items-center gap-2 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-semibold text-brand-700 hover:border-brand-300"
          >
            <Download className="size-4" /> {filename}
          </button>
        </div>
      </div>
    </section>
  )
}

function DropZone({
  file,
  onFile,
  onUpload,
  uploading,
  error,
  hint,
}: {
  file: File | null
  onFile: (f: File | null) => void
  onUpload: () => void
  uploading: boolean
  error: string
  hint: string
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  function onDrop(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files?.[0]
    if (f) onFile(f)
  }

  return (
    <section className="rounded-2xl border border-ink-200 bg-white p-5">
      <h2 className="mb-3 font-semibold text-ink-900">2. Sube tu CSV</h2>

      <label
        htmlFor="csv-seo-file"
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-12 transition-colors ${
          dragOver
            ? 'border-brand-500 bg-brand-50'
            : 'border-ink-200 bg-ink-50 hover:border-brand-400 hover:bg-brand-50/50'
        }`}
      >
        <Upload className="size-10 text-ink-400" />
        <div className="text-center">
          <p className="font-semibold text-ink-700">
            {file ? file.name : 'Arrastra el CSV aquí o haz clic para seleccionar'}
          </p>
          <p className="mt-1 text-xs text-ink-500">{hint}</p>
        </div>
        <input
          ref={inputRef}
          id="csv-seo-file"
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            onFile(e.target.files?.[0] ?? null)
            e.target.value = ''
          }}
        />
      </label>

      {file && (
        <div className="mt-4 flex items-center justify-between rounded-lg border border-ink-200 bg-ink-50 p-3 text-sm">
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium text-ink-900">{file.name}</div>
            <div className="text-xs text-ink-500">
              {(file.size / 1024).toFixed(1)} KB
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onFile(null)}
              disabled={uploading}
              className="grid size-8 place-items-center rounded-md border border-ink-200 bg-white text-ink-600 hover:bg-ink-100 disabled:opacity-50"
              title="Quitar archivo"
            >
              <X className="size-4" />
            </button>
            <button
              type="button"
              onClick={onUpload}
              disabled={uploading}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {uploading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Upload className="size-4" />
              )}
              {uploading ? 'Procesando…' : 'Subir e importar'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 size-4" />
          {error}
        </div>
      )}
    </section>
  )
}

function Stat({
  label,
  value,
  color = 'ink',
}: {
  label: string
  value: number
  color?: 'ink' | 'emerald' | 'brand' | 'red' | 'amber'
}) {
  const colors: Record<string, string> = {
    ink: 'text-ink-900 bg-ink-50',
    emerald: 'text-emerald-700 bg-emerald-50',
    brand: 'text-brand-700 bg-brand-50',
    red: 'text-red-700 bg-red-50',
    amber: 'text-amber-700 bg-amber-50',
  }
  return (
    <div className={`rounded-xl p-3 text-center ${colors[color]}`}>
      <div className="text-2xl font-extrabold">
        {value.toLocaleString('es-MX')}
      </div>
      <div className="text-xs uppercase tracking-wider opacity-70">{label}</div>
    </div>
  )
}

function ListSection({
  title,
  items,
  variant,
}: {
  title: string
  items: string[]
  variant: 'red' | 'amber'
}) {
  const styles =
    variant === 'red'
      ? {
          header: 'text-red-700',
          item: 'border-red-100 bg-red-50 text-red-800',
        }
      : {
          header: 'text-amber-700',
          item: 'border-amber-100 bg-amber-50 text-amber-800',
        }
  return (
    <div className="mt-4">
      <h3 className={`mb-2 text-sm font-semibold ${styles.header}`}>
        {title} ({items.length})
      </h3>
      <ul className="max-h-64 space-y-1 overflow-y-auto text-xs">
        {items.map((it, i) => (
          <li
            key={i}
            className={`break-words rounded border p-2 font-mono ${styles.item}`}
          >
            {it}
          </li>
        ))}
      </ul>
    </div>
  )
}

export default SeoTools
